/**
 * SequencingDeviceTemplatesPage — iter150AG.onboarding admin CRUD.
 *
 * Admins maintain the lookup table that the onboarding wizard uses to
 * pick the CP `device_template_external_id` + `gateway_model` for a
 * compressor. The resolver scores rows by specificity (each non-empty
 * match field that matches the compressor scores 100 points; the
 * `priority` field breaks ties; one row may also be marked `is_default`
 * for the fallback case).
 *
 * Why "manage in CAP" and not "list from CP":
 *   We deliberately keep this catalog in CAP so the customer-success
 *   team can pre-register new compressor / inverter combos without
 *   needing a CP code release. The CP just receives the resolved
 *   `device_template_external_id` string at provision time.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Plus, Pencil, Trash, Funnel, CheckCircle, Warning,
  CircleNotch, BookOpenText, Star,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  seqListDeviceTemplates, seqListCpDeviceTemplates, seqCreateDeviceTemplate,
  seqUpdateDeviceTemplate, seqDeleteDeviceTemplate,
  seqResolveDeviceTemplate,
} from "@/lib/sequencingApi";


const _BLANK = {
  name: "", device_template_external_id: "",
  plc_type: "", inverter_type: "", product_name_pattern: "",
  priority: 0, is_default: false, notes: "",
};


export default function SequencingDeviceTemplatesPage() {
  const [items, setItems]       = useState([]);
  const [cpTemplates, setCpTemplates] = useState([]);
  const [cpMode, setCpMode]     = useState("");
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState("");
  const [editing, setEditing]   = useState(null);  // null = closed, {} = create, {id…} = edit
  const [previewIn, setPreviewIn]   = useState({ plc_type: "", inverter_type: "", product_name: "" });
  const [previewOut, setPreviewOut] = useState(null);

  async function refresh() {
    setLoading(true); setErr("");
    try {
      const [maps, cpResp] = await Promise.all([
        seqListDeviceTemplates(),
        seqListCpDeviceTemplates().catch(() => ({ items: [], mode: "unknown" })),
      ]);
      setItems(maps.items || []);
      setCpTemplates(cpResp.items || []);
      setCpMode(cpResp.mode || "");
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || String(e));
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  async function save(form) {
    try {
      if (form.id) {
        await seqUpdateDeviceTemplate(form.id, form);
        toast.success("Mapping updated");
      } else {
        await seqCreateDeviceTemplate(form);
        toast.success("Mapping created");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Save failed");
    }
  }

  async function remove(row) {
    if (!window.confirm(`Delete mapping "${row.name}"?`)) return;
    try {
      await seqDeleteDeviceTemplate(row.id);
      toast.success("Mapping deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Delete failed");
    }
  }

  async function runPreview() {
    try {
      const r = await seqResolveDeviceTemplate({
        plc_type:      previewIn.plc_type.trim() || undefined,
        inverter_type: previewIn.inverter_type.trim() || undefined,
        product_name:  previewIn.product_name.trim() || undefined,
      });
      setPreviewOut(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Preview failed");
    }
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="seq-device-templates">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <Link to="/sequencing" data-testid="dtm-back"
              className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={16} weight="bold" />
        </Link>
        <BookOpenText size={22} weight="fill" className="text-blue-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">
            Device template mapping
          </h1>
          <p className="text-[11px] text-slate-500 truncate">
            Maps a compressor's (PLC × inverter × model) to a CP <code>device_template_external_id</code>.
            User-facing type names hide the raw template ID from operators.
          </p>
        </div>
        <button data-testid="dtm-add" onClick={() => setEditing({ ..._BLANK })}
                className="text-xs px-3 py-1.5 rounded-sm border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-1">
          <Plus size={12} weight="bold" /> New mapping
        </button>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-5 space-y-5">
        {err && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-3 rounded-sm"
               data-testid="dtm-error">{err}</div>
        )}

        {/* Resolver preview card */}
        <section data-testid="dtm-resolver-preview"
                 className="bg-white border border-slate-200 rounded-sm p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Funnel size={14} className="text-blue-700" />
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">Try the resolver</h2>
          </div>
          <p className="text-[11px] text-slate-500">
            Type a compressor's PLC / inverter / product name and see which mapping the wizard would pick.
          </p>
          <div className="grid grid-cols-3 gap-2">
            <input data-testid="dtm-preview-plc"
                   placeholder="PLC type (e.g. delta)"
                   value={previewIn.plc_type}
                   onChange={e => setPreviewIn(p => ({ ...p, plc_type: e.target.value }))}
                   className="text-xs px-2 py-1.5 border border-slate-300 rounded-sm" />
            <input data-testid="dtm-preview-inverter"
                   placeholder="Inverter type"
                   value={previewIn.inverter_type}
                   onChange={e => setPreviewIn(p => ({ ...p, inverter_type: e.target.value }))}
                   className="text-xs px-2 py-1.5 border border-slate-300 rounded-sm" />
            <input data-testid="dtm-preview-product"
                   placeholder="Product name (e.g. US40-VSD)"
                   value={previewIn.product_name}
                   onChange={e => setPreviewIn(p => ({ ...p, product_name: e.target.value }))}
                   className="text-xs px-2 py-1.5 border border-slate-300 rounded-sm" />
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="dtm-preview-run" onClick={runPreview}
                    className="text-xs px-3 py-1.5 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
              Resolve
            </button>
            {previewOut && (
              <div data-testid="dtm-preview-out" className="text-[11px]">
                {previewOut.matched ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700">
                    <CheckCircle size={11} weight="fill" />
                    <b>{previewOut.matched.name}</b>
                    <span className="text-slate-500 font-mono">
                      → {previewOut.matched.device_template_external_id}
                    </span>
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-amber-700">
                    <Warning size={11} weight="fill" /> No mapping matched
                  </span>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Mapping list */}
        {loading && items.length === 0 ? (
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <CircleNotch size={12} className="animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-6 text-center text-slate-500 text-xs"
               data-testid="dtm-empty">
            No mappings yet. Click <b>New mapping</b> to add your first one.
          </div>
        ) : (
          <table className="w-full text-xs bg-white border border-slate-200 rounded-sm"
                 data-testid="dtm-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Match (PLC / Inv / Product)</th>
                <th className="text-left px-3 py-2">CP template</th>
                <th className="text-right px-3 py-2">Prio</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(row => {
                const cpTpl = cpTemplates.find(t => (t.external_id || t.id) === row.device_template_external_id);
                return (
                <tr key={row.id} data-testid={`dtm-row-${row.id}`}
                    className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    {row.is_default && <Star size={11} weight="fill"
                                              className="inline text-amber-500 -mt-0.5 mr-1" title="Default fallback" />}
                    <span className="font-bold text-slate-900">{row.name}</span>
                    {row.notes && <div className="text-[10px] text-slate-500">{row.notes}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-600">
                    {row.plc_type      || <span className="text-slate-300">·</span>} /
                    {" "}{row.inverter_type || <span className="text-slate-300">·</span>} /
                    {" "}{row.product_name_pattern || <span className="text-slate-300">·</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-slate-700">{row.device_template_external_id}</div>
                    {cpTpl?.name && <div className="text-[10px] text-slate-500">{cpTpl.name}</div>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{row.priority}</td>
                  <td className="px-3 py-2 text-right">
                    <button data-testid={`dtm-edit-${row.id}`}
                            onClick={() => setEditing({ ...row })}
                            className="text-[11px] px-1.5 py-1 text-slate-500 hover:text-blue-700">
                      <Pencil size={11} />
                    </button>
                    <button data-testid={`dtm-delete-${row.id}`}
                            onClick={() => remove(row)}
                            className="text-[11px] px-1.5 py-1 text-slate-500 hover:text-rose-700">
                      <Trash size={11} />
                    </button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        )}
      </main>

      {editing && (
        <EditModal initial={editing}
                    cpTemplates={cpTemplates}
                    cpMode={cpMode}
                    onClose={() => setEditing(null)}
                    onSave={save} />
      )}
    </div>
  );
}


function EditModal({ initial, cpTemplates, cpMode, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const isEdit = !!initial.id;
  const hasCpCatalog = Array.isArray(cpTemplates) && cpTemplates.length > 0;

  function handleSave() {
    if (!form.name?.trim()) return toast.error("Name is required");
    if (!form.device_template_external_id?.trim()) return toast.error("Pick a CP device template");
    onSave({
      ...form,
      name: form.name.trim(),
      device_template_external_id: form.device_template_external_id.trim(),
      plc_type: (form.plc_type || "").trim(),
      inverter_type: (form.inverter_type || "").trim(),
      product_name_pattern: (form.product_name_pattern || "").trim(),
      priority: Number(form.priority) || 0,
    });
  }

  const selectedCp = cpTemplates.find(t => (t.external_id || t.id) === form.device_template_external_id);

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-30 flex items-center justify-center px-4"
         data-testid="dtm-modal" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className="bg-white rounded-sm w-full max-w-lg border border-slate-200 shadow-lg">
        <header className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <h2 className="text-sm font-bold font-['Chivo'] text-slate-900">
            {isEdit ? "Edit mapping" : "New mapping"}
          </h2>
        </header>
        <div className="px-4 py-4 space-y-3 text-xs">
          <Field label="User-facing type name" required>
            <input data-testid="dtm-form-name"
                    type="text" value={form.name}
                    onChange={e => set("name", e.target.value)}
                    placeholder="e.g. VSD Master"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded-sm" />
            <p className="mt-1 text-[10px] text-slate-500">
              Shown in the onboarding wizard. Internal template ID is hidden from end-users.
            </p>
          </Field>

          <Field label="CP device template" required>
            {hasCpCatalog ? (
              <>
                <select
                  data-testid="dtm-form-tpl"
                  value={form.device_template_external_id}
                  onChange={e => set("device_template_external_id", e.target.value)}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded-sm font-mono">
                  <option value="">— select —</option>
                  {cpTemplates.map(t => (
                    <option key={t.external_id || t.id}
                            value={t.external_id || t.id}>
                      {t.name} ({t.external_id || t.id})
                    </option>
                  ))}
                </select>
                {selectedCp?.description && (
                  <p data-testid="dtm-form-tpl-desc"
                      className="mt-1 text-[10px] text-slate-500">
                    {selectedCp.description}
                  </p>
                )}
              </>
            ) : (
              <>
                <input data-testid="dtm-form-tpl"
                        type="text" value={form.device_template_external_id}
                        onChange={e => set("device_template_external_id", e.target.value)}
                        placeholder="e.g. vsd_master"
                        className="w-full px-2 py-1.5 border border-slate-300 rounded-sm font-mono" />
                <p className="mt-1 text-[10px] text-amber-700">
                  <Warning size={10} weight="fill" className="inline -mt-0.5 mr-0.5" />
                  Control Plane catalog unavailable (mode: {cpMode || "unknown"}). Free-text fallback —
                  please coordinate with the CP team before saving.
                </p>
              </>
            )}
          </Field>

          <div className="border-t border-slate-100 pt-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">
              Match fields — leave blank for wildcard
            </p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="PLC type">
                <input data-testid="dtm-form-plc"
                        type="text" value={form.plc_type}
                        onChange={e => set("plc_type", e.target.value)}
                        placeholder="delta"
                        className="w-full px-2 py-1.5 border border-slate-300 rounded-sm" />
              </Field>
              <Field label="Inverter type">
                <input data-testid="dtm-form-inverter"
                        type="text" value={form.inverter_type}
                        onChange={e => set("inverter_type", e.target.value)}
                        placeholder="frequency"
                        className="w-full px-2 py-1.5 border border-slate-300 rounded-sm" />
              </Field>
              <Field label="Product pattern (substring)">
                <input data-testid="dtm-form-product"
                        type="text" value={form.product_name_pattern}
                        onChange={e => set("product_name_pattern", e.target.value)}
                        placeholder="VSD"
                        className="w-full px-2 py-1.5 border border-slate-300 rounded-sm" />
              </Field>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Priority (tie-break)">
              <input data-testid="dtm-form-priority"
                      type="number" value={form.priority}
                      onChange={e => set("priority", e.target.value)}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-sm font-mono" />
            </Field>
            <label className="flex items-end gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.is_default}
                      onChange={e => set("is_default", e.target.checked)}
                      data-testid="dtm-form-default" />
              <span className="text-[11px]">Default fallback</span>
            </label>
          </div>
          <Field label="Notes (internal)">
            <textarea data-testid="dtm-form-notes"
                      value={form.notes || ""}
                      onChange={e => set("notes", e.target.value)}
                      rows={2}
                      className="w-full px-2 py-1.5 border border-slate-300 rounded-sm" />
          </Field>
        </div>
        <footer className="px-4 py-3 border-t border-slate-200 flex items-center gap-2">
          <button data-testid="dtm-form-cancel" onClick={onClose}
                  className="text-xs px-3 py-1.5 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button data-testid="dtm-form-save" onClick={handleSave}
                  className="ml-auto text-xs px-3 py-1.5 rounded-sm border border-blue-600 bg-blue-600 text-white hover:bg-blue-700">
            {isEdit ? "Save changes" : "Create mapping"}
          </button>
        </footer>
      </div>
    </div>
  );
}


function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">
        {label}{required && <span className="text-rose-600 ml-0.5">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
